import React from 'react';
import { injectIntl, WrappedComponentProps } from 'react-intl';
import { cn } from 'cn';
import { Checkbox as UICheckbox } from '../../components/ui/checkbox';
import Info from '../info';

type CheckboxModel = {
  /** Texto ao lado da caixa. Um `<FormattedMessage>` é traduzido. */
  label?: React.ReactNode;
  value?: unknown;
  requestChange?: (value: boolean) => void;
};

type CheckboxProps = Omit<
  React.ComponentPropsWithoutRef<'div'>,
  'onChange' | 'color'
> & {
  /** Campo do formulário: `label`, `value` (marcado se verdadeiro) e `requestChange`, chamado com o booleano. */
  model: CheckboxModel;
  /** Chamado com o booleano novo (não com o evento). */
  onChange?: (checked: boolean) => void;
  /** Chamado depois de cada mudança, sem argumentos. */
  getAlteracao?: () => void;
  disabled?: boolean;
  required?: boolean;
  autoFocus?: boolean;
  /** Lado a lado com o próximo checkbox, em vez de um por linha. */
  inline?: boolean;
  /** Mostra o ícone "i" de ajuda depois do texto, com `labelHint`. */
  showHint?: boolean;
  /** Texto da ajuda do `showHint`. */
  labelHint?: React.ReactNode;
  /** Id do ícone de ajuda. */
  idHint?: string;
  /** Cursor da caixa (ex.: `'not-allowed'`). */
  cursor?: React.CSSProperties['cursor'];
  /** Classes do wrapper. Use para margem de alinhamento. */
  className?: string;
  /** Estilo do `<label>` que envolve a caixa e o texto. */
  styleLabel?: React.CSSProperties;
  /** Ignorada: herança do componente antigo. */
  addons?: unknown;
  /** Ignorada: herança do componente antigo. */
  clearable?: unknown;
  /** Ignorada: herança do componente antigo. */
  formStyle?: unknown;
  /** Ignorada: herança do componente antigo. */
  height?: unknown;
  /** Ignorada: herança do componente antigo. */
  width?: unknown;
  /** Ignorada: herança do componente antigo. */
  color?: unknown;
};

/**
 * Caixa de seleção ligada a um campo do formulário (`model`), com texto clicável ao lado.
 * @import import Checkbox from 'src/componentes/checkbox';
 */
function Checkbox({
  cursor,
  intl,
  model,
  disabled,
  getAlteracao,
  onChange,
  showHint = false,
  idHint = 'defaultIdHint',
  labelHint = 'defaultLabelHint',
  inline,
  required,
  autoFocus,
  className,
  styleLabel,
  addons,
  clearable,
  formStyle,
  height,
  width,
  color,
  ...rest
}: CheckboxProps & WrappedComponentProps) {
  const handleChange = (checked: boolean | 'indeterminate') => {
    const value = checked === true;
    model.requestChange && model.requestChange(value);
    onChange && onChange(value);

    if (getAlteracao != null) {
      getAlteracao();
    }
  };

  const hasLabel = !!model.label;
  const labelElement = model.label as React.ReactElement | undefined;

  return (
    <div
      className={cn(inline ? 'tw:me-3 tw:inline-flex' : 'tw:block', className)}
      {...rest}
    >
      <label
        className={cn(
          'tw:mb-0 tw:inline-flex tw:gap-1.5',
          hasLabel ? 'tw:items-start' : 'tw:items-center'
        )}
        style={styleLabel}
      >
        <UICheckbox
          checked={!!model.value}
          disabled={disabled}
          required={required}
          autoFocus={autoFocus}
          onCheckedChange={handleChange}
          className={cn(hasLabel && 'tw:mt-0.75')}
          style={cursor ? { cursor } : undefined}
        />
        {model.label &&
          (labelElement?.props?.id
            ? intl.formatMessage({ ...labelElement.props })
            : model.label)}
        {showHint && (
          <Info
            id={idHint}
            className="tw:ps-1"
            placement="right"
            text={labelHint}
          />
        )}
      </label>
    </div>
  );
}

export default injectIntl(Checkbox);
